import React, { useState, useRef } from 'react';
import { MediaItem, MediaType } from '../../../api/structs/media';
import { MediaOutputSpec } from '../../../api/structs/media-spec';
import { ClipStyleSchema, createEmptyClipStyleSchema } from '../../../api/clipstyleSchema';
import API, { ClipAPI } from '../../../api/api';
import { AddMediaModal } from '../../modals';
import SceneRegenerateModal, { SceneRegenerateTab } from '../../modals/SceneRegenerateModal';
import MediaLibraryPickerModal from '../../modals/MediaLibraryPickerModal';
import { useToast } from '../../../hooks/useToast';
import MediaEditorSceneCard from './MediaEditorSceneCard';
import MediaEditorSceneDetailPanel from './MediaEditorSceneDetailPanel';
import MediaEditorTransitionChip from './MediaEditorTransitionChip';
import { SceneGroup, groupMediaByScene } from './MediaEditorSection.helpers';
import {
  RegenerateWatchMap,
  beginRegenerateWatch,
  expireRegenerateWatches,
  sceneRegenerateStatus,
  settleRegenerateWatches,
} from './regenerateWatch';

interface MediaEditorSectionProps {
  clipId: string;
  images: MediaItem[];
  aiVideos: MediaItem[];
  audios: MediaItem[];
  clipStyle: string;
  clipMetadata?: Record<string, any>;
  onRefresh: () => void;
  onPendingSaveChange?: (delta: number) => void;
  onDraftDirty?: () => void;
  mediaProfile: { image?: MediaOutputSpec; video?: MediaOutputSpec; audio?: MediaOutputSpec };
}

const MediaEditorSection: React.FC<MediaEditorSectionProps> = ({
  clipId,
  images,
  aiVideos,
  audios,
  clipStyle,
  clipMetadata,
  onRefresh,
  onPendingSaveChange,
  onDraftDirty,
  mediaProfile,
}) => {
  const toast = useToast();
  const [selectedSceneId, setSelectedSceneId] = useState<string | null>(null);
  const [scenes, setScenes] = useState<SceneGroup[]>(() => groupMediaByScene(images, aiVideos));
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMediaType, setAddMediaType] = useState<MediaType>('image');
  const [styleSchema, setStyleSchema] = useState<ClipStyleSchema>(() => createEmptyClipStyleSchema(clipStyle));
  // Scene-level regenerate: stores the scene ID + starting tab, never the scene
  // object. The modal must see the LIVE scene (re-resolved by id each render)
  // so an in-place frame regeneration surfaces inside the open modal — that
  // prop drift is what arms its "Use new opening/closing frame" buttons.
  const [regenScene, setRegenScene] = useState<{ sceneId: string; tab: SceneRegenerateTab } | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<MediaItem | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const dragIdx = useRef<number | null>(null);
  const [dragTargetIdx, setDragTargetIdx] = useState<number | null>(null);
  // In-flight regenerations, keyed by media id. Held HERE, not in the modal:
  // the modal unmounts the moment a regeneration is submitted, and this state
  // has to outlive it — it is the only thing standing between "Regenerate" and
  // a result that may be minutes away. See regenerateWatch.ts.
  const [regenWatches, setRegenWatches] = useState<RegenerateWatchMap>({});

  React.useEffect(() => {
    setScenes(groupMediaByScene(images, aiVideos));
  }, [images, aiVideos]);

  // Every clip refetch (WS UpdateMediaItemFileURL / UpdateMediaItemMetadata →
  // App refreshClips → new media props) is a chance for a watch to settle. A
  // FAILED regeneration arrives here as new metadata on the canonical item —
  // its file_url is deliberately unchanged — so metadata is checked too.
  React.useEffect(() => {
    setRegenWatches((prev) => settleRegenerateWatches(prev, [...images, ...aiVideos]));
  }, [images, aiVideos]);

  // Watchdog. Nothing else can end a wait for a result that never arrives (a
  // killed backend goroutine emits no event at all), and a spinner with no
  // terminal state is how the original bug felt in the first place.
  React.useEffect(() => {
    if (Object.keys(regenWatches).length === 0) return;
    const timer = window.setInterval(() => {
      setRegenWatches((prev) => expireRegenerateWatches(prev));
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [regenWatches]);

  const beginRegenerate = React.useCallback(
    (mediaId: string) => {
      const item = [...images, ...aiVideos].find((entry) => entry.id === mediaId);
      if (!item) return;
      setRegenWatches((prev) => beginRegenerateWatch(prev, item));
    },
    [images, aiVideos]
  );

  React.useEffect(() => {
    let cancelled = false;
    API.getClipStyleSchema(clipStyle)
      .then((schema) => { if (!cancelled) setStyleSchema(schema); })
      .catch(() => { if (!cancelled) setStyleSchema(createEmptyClipStyleSchema(clipStyle)); });
    return () => { cancelled = true; };
  }, [clipStyle]);

  const getOutputSpec = (type: MediaType): MediaOutputSpec | undefined => {
    if (type === 'image') return mediaProfile.image;
    if (type === 'ai_video') return mediaProfile.video;
    if (type === 'audio') return mediaProfile.audio;
  };

  const getMediaIndex = (item: MediaItem): number => {
    const bucket = item.type === 'ai_video' ? aiVideos : item.type === 'audio' ? audios : images;
    return bucket.findIndex((candidate) => candidate.id === item.id);
  };

  const withPendingSave = async (task: () => Promise<void>) => {
    onPendingSaveChange?.(1);
    try {
      await task();
    } finally {
      onPendingSaveChange?.(-1);
    }
  };

  const handleDragStart = (idx: number) => { dragIdx.current = idx; };
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragTargetIdx(idx);
  };
  const handleDrop = async (dropIdx: number) => {
    const fromIdx = dragIdx.current;
    if (fromIdx === null || fromIdx === dropIdx) {
      dragIdx.current = null;
      setDragTargetIdx(null);
      return;
    }
    const reordered = [...scenes];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setScenes(reordered);
    onDraftDirty?.();
    dragIdx.current = null;
    setDragTargetIdx(null);
    try {
      await withPendingSave(async () => {
        await Promise.all(
          reordered.flatMap((scene, idx) =>
            scene.allItems.map((item) =>
              API.replaceMediaMetadata(item.id, { ...item.metadata, order: idx })
            )
          )
        );
      });
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to reorder: ${error.message}`, level: 'error' });
    }
  };

  const handleMoveScene = async (fromIdx: number, toIdx: number) => {
    const reordered = [...scenes];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setScenes(reordered);
    onDraftDirty?.();
    try {
      await withPendingSave(async () => {
        await Promise.all(
          reordered.flatMap((scene, idx) =>
            scene.allItems.map((item) =>
              API.replaceMediaMetadata(item.id, { ...item.metadata, order: idx })
            )
          )
        );
      });
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to reorder: ${error.message}`, level: 'error' });
    }
  };

  const handleDelete = async (item: MediaItem) => {
    try {
      await API.deleteMediaItem(item.id);
      onRefresh();
    } catch (error: any) {
      toast({ text: `Failed to delete: ${error.message}`, level: 'error' });
    }
  };

  const selectedScene = scenes.find((s) => s.sceneId === selectedSceneId) || null;

  // Initial tab: the video form when the scene has one, else straight to the
  // frame form — the modal itself clamps if the scene shape shifts meanwhile.
  const openSceneRegenerate = (scene: SceneGroup) =>
    setRegenScene({ sceneId: scene.sceneId, tab: scene.video ? 'video' : 'frames' });

  const liveRegenScene = regenScene
    ? scenes.find((scene) => scene.sceneId === regenScene.sceneId) ?? null
    : null;

  return (
    <>
      {/* Storyboard header with expand toggle */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-mono">Storyboard</span>
        <button
          onClick={() => setIsExpanded((e) => !e)}
          title={isExpanded ? 'Collapse storyboard' : 'Expand storyboard'}
          className="text-[10px] text-zinc-600 hover:text-zinc-300 font-mono transition-colors"
        >
          {isExpanded ? '⊟ Collapse' : '⊞ Expand'}
        </button>
      </div>

      {/* Filmstrip */}
      <div className={isExpanded ? 'flex flex-wrap gap-3 pb-2' : 'flex gap-2 overflow-x-auto pb-2 snap-x'}>
        {scenes.map((scene, idx) => (
          <React.Fragment key={scene.sceneId}>
            <MediaEditorSceneCard
              scene={scene}
              index={idx}
              regenerateStatus={sceneRegenerateStatus(
                regenWatches,
                scene.allItems.map((entry) => entry.id)
              )}
              isSelected={selectedSceneId === scene.sceneId}
              isDragTarget={dragTargetIdx === idx}
              onClick={() =>
                setSelectedSceneId((prev) => (prev === scene.sceneId ? null : scene.sceneId))
              }
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={() => handleDrop(idx)}
              onRegenerate={() => openSceneRegenerate(scene)}
              onReplace={(item) => setReplaceTarget(item)}
              onDelete={handleDelete}
              canMoveLeft={idx > 0}
              canMoveRight={idx < scenes.length - 1}
              onMoveLeft={() => handleMoveScene(idx, idx - 1)}
              onMoveRight={() => handleMoveScene(idx, idx + 1)}
            />
            {!isExpanded && idx < scenes.length - 1 && (
              <MediaEditorTransitionChip scene={scene} onRefresh={onRefresh} />
            )}
          </React.Fragment>
        ))}

        {/* Add scene */}
        <button
          onClick={() => { setAddMediaType('image'); setShowAddModal(true); }}
          className="w-32 min-h-[104px] shrink-0 border border-dashed border-white/10 hover:border-white/25 flex flex-col items-center justify-center gap-1 text-zinc-700 hover:text-zinc-400 transition-colors snap-start font-mono text-xs"
        >
          <span className="text-xl font-light">+</span>
          <span>add</span>
        </button>
      </div>

      {/* Detail panel */}
      {selectedScene && (
        <MediaEditorSceneDetailPanel
          key={selectedSceneId}
          scene={selectedScene}
          index={scenes.indexOf(selectedScene)}
          styleSchema={styleSchema}
          getMediaIndex={getMediaIndex}
          clipId={clipId}
          clipStyle={clipStyle}
          clipMetadata={clipMetadata}
          carouselImages={images}
          audios={audios}
          regenWatches={regenWatches}
          onRefresh={onRefresh}
          onPendingSaveChange={onPendingSaveChange}
          onDraftDirty={onDraftDirty}
          onClose={() => setSelectedSceneId(null)}
          onRegenerateScene={() => openSceneRegenerate(selectedScene)}
        />
      )}

      <AddMediaModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        clipId={clipId}
        onSuccess={onRefresh}
        defaultType={addMediaType}
        mediaProfile={mediaProfile}
      />

      {/* Keyed on the scene so a REOPEN takes fresh frame snapshots; the scene
          prop itself stays live across renders while it is open (a deleted
          scene simply unmounts the modal). */}
      {regenScene && liveRegenScene && (
        <SceneRegenerateModal
          key={`scene-regen-${regenScene.sceneId}`}
          isOpen
          onClose={() => setRegenScene(null)}
          scene={liveRegenScene}
          initialTab={regenScene.tab}
          getOutputSpec={getOutputSpec}
          regenWatches={regenWatches}
          onRegenerateStarted={beginRegenerate}
          onRefresh={onRefresh}
        />
      )}

      {replaceTarget && (
        <MediaLibraryPickerModal
          isOpen={!!replaceTarget}
          onClose={() => setReplaceTarget(null)}
          filterType={replaceTarget.type === 'ai_video' ? 'video' : replaceTarget.type === 'audio' ? 'audio' : 'image'}
          onSelect={async (selected) => {
            const kind: 'image' | 'ai_video' | 'audio' =
              replaceTarget.type === 'ai_video' ? 'ai_video' : replaceTarget.type === 'audio' ? 'audio' : 'image';
            const mediaIndex = getMediaIndex(replaceTarget);
            try {
              await ClipAPI.swapClipMedia(clipId, {
                kind,
                media_index: Math.max(0, mediaIndex),
                new_media_id: selected.id,
                // The slot named by the id it holds — the index is a legacy
                // fallback computed from a delivered order the store never
                // guaranteed.
                old_media_id: replaceTarget.id,
              });
              toast({ text: `Replaced with ${selected.name || selected.id}`, level: 'success' });
              onRefresh();
            } catch (error: any) {
              toast({ text: `Failed to replace: ${error.message}`, level: 'error' });
            }
            setReplaceTarget(null);
          }}
        />
      )}
    </>
  );
};

export default MediaEditorSection;
